import React, { useState } from "react";
import { Button, Typography } from "@mui/material";
import "./ImportHistoryList.scss";
import CachedIcon from "@mui/icons-material/Cached";

import { TImportedFile } from "../../../shared/services/massImportLVL1/massImportService";
import { format } from "date-fns";
import { DashboardService } from "../../../shared/services/dashboardService";
import * as XLSX from "xlsx";

interface ImportHistoryListProps {
    history: TImportedFile[];
    onRollback: (file: TImportedFile) => void;
    isLoading: boolean;
    error: string | null;
    onRefresh: () => void;
}

const ImportHistoryList: React.FC<ImportHistoryListProps> = ({ history, onRollback, isLoading, error, onRefresh }) => {
    const [selectedFile, setSelectedFile] = useState<TImportedFile | null>(null);

    const handleRollbackClick = (file: TImportedFile) => {
        setSelectedFile(file);
    };

    const handleConfirmRollback = async () => {
        if (selectedFile) {
            await onRollback({
                ...selectedFile,
                isImportRollbacked: true,
            });
            setSelectedFile(null);
            onRefresh();
        }
    };

    const handleCancelRollback = () => {
        setSelectedFile(null);
    };

    if (isLoading) {
        return (
            <div className="drag-and-drop__loading">
                <CachedIcon className="drag-and-drop__loading-icon" />
                <p>Loading...</p>
            </div>
        );
    }

    if (error) {
        return <p className="error-message">{error}</p>;
    }

    const handleEventsClick = async (fileId: number) => {
        DashboardService.getExportEventsForImportedFile(fileId).subscribe(
            (response) => {
                const data = response.data.model;
    
                const metadata = [
                    ["File ID", "File Name", "User", "Date and Time"],
                    [data.fileId, data.fileName, data.user, new Date(data.importedDate).toLocaleString()]
                ];
    
                const metadataSheet = XLSX.utils.aoa_to_sheet(metadata);
                const emptyRowIndex = metadata.length;
                const eventsData = data.exportEventFromImportedFiles;
                XLSX.utils.sheet_add_json(metadataSheet, eventsData, { origin: emptyRowIndex + 1 });
    
                const range = XLSX.utils.decode_range(metadataSheet["!ref"] || "A1");
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const cell = XLSX.utils.encode_cell({ r: 0, c: C });
                    if (!metadataSheet[cell]) continue;
                    metadataSheet[cell] = {
                        ...metadataSheet[cell],
                        s: {
                            font: { bold: true }
                        }
                    };
                }
    
                const finalSheet = metadataSheet;
                const workbook: XLSX.WorkBook = { Sheets: { Events: finalSheet }, SheetNames: ["Events"] };
                const filename: string = "ExportEventsForImportedFile.xlsx";
    
                XLSX.writeFile(workbook, filename);
            },
            (error) => {
                console.error("Error fetching events:", error);
            }
        );
    };

    return (
        <section className="import-history-list">
            {history.length > 0 ? (
                history.map((file) => (
                    <div
                        key={file.id}
                        className={`import-history-item-container ${selectedFile && selectedFile.id !== file.id ? "disabled" : ""
                            }`}
                    >
                        {selectedFile?.id === file.id && (
                            <Typography variant="body1" className="warning-message">
                                You are about to rollback {file.eventsCount} imported events.
                            </Typography>
                        )}
                        <div
                            className={`import-history-item ${selectedFile?.id === file.id ? "selected" : ""} ${file.isImportRollbacked ? "rolledbacked" : ""
                                }`}
                        >
                            <div className="import-history-details">
                                <p>
                                    {format(new Date(file.createdDate), "dd/MM/yyyy, h.mm a")}
                                    {file.isImportRollbacked ? (
                                        <span className="rolledback-label">Rolledback</span>
                                    ) : (
                                        <span className="title-label">{file.fileName}</span>
                                    )}
                                </p>
                                <p>
                                    {file.createdBy} &bull; <span style={{ cursor: 'pointer' }} onClick={() => handleEventsClick(file.id)}>{file.eventsCount} events</span>
                                </p>
                            </div>
                            {file.isImportRollbacked ? (
                                <Button variant="outlined" color="secondary" disabled>
                                    Rollback
                                </Button>
                            ) : selectedFile?.id === file.id ? (
                                <div className="rollback-confirmation">
                                    <Button variant="outlined" color="primary" onClick={handleCancelRollback}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        className="button-confirm"
                                        onClick={handleConfirmRollback}
                                    >
                                        Confirm
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => handleRollbackClick(file)}
                                    disabled={!!selectedFile}
                                >
                                    Rollback
                                </Button>
                            )}
                        </div>
                    </div>
                ))
            ) : (
                <p>No history available.</p>
            )}
        </section>
    );
};

export default ImportHistoryList;
